import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { HeartHandshake, Plus, Loader2, Trash2, Pencil, Info, Download, SlidersHorizontal, Layers, Globe2, Sparkles } from "lucide-react";
import { AppShell } from "@/components/app/AppShell";
import { EmptyState } from "@/components/EmptyState";
import { SweatEquityCard } from "@/components/SweatEquityCard";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/DatePicker";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { PlanBadge } from "@/components/PlanBadge";
import { needsUpgrade, openUpgrade } from "@/lib/planFeatures";

const uid = () => (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `r_${Math.random().toString(36).slice(2)}`);
const EMPTY = { person_name: "", role: "", region: "", experience_level: "", market_salary: "", actual_salary: "", start_date: "", end_date: "", equity_pct: "", vesting_years: 4, cliff_months: 12, raises: [], notes: "" };

export default function SweatEquity() {
  const { company } = useAuth();
  const [data, setData] = useState({ entries: [], summary: {} });
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [benchmarks, setBenchmarks] = useState(null);
  const [reporting, setReporting] = useState(false);

  // Role tracks
  const [tracks, setTracks] = useState({ presets: [], custom: [] });
  const [tracksOpen, setTracksOpen] = useState(false);
  const [trackForm, setTrackForm] = useState({ name: "", vesting_years: 4, cliff_months: 12 });
  const [trackEditId, setTrackEditId] = useState(null);

  // Benchmark editor
  const [benchOpen, setBenchOpen] = useState(false);
  const [benchDraft, setBenchDraft] = useState({});
  const [savingBench, setSavingBench] = useState(false);

  // US market benchmarks (levels.fyi-style)
  const [usBench, setUsBench] = useState(null);
  const [usRole, setUsRole] = useState("");
  const [usLevel, setUsLevel] = useState("");
  const [aiEstimate, setAiEstimate] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);
  const aiReqId = useRef(0);
  const [usBenchOpen, setUsBenchOpen] = useState(false);
  const [usBenchDraft, setUsBenchDraft] = useState({});
  const [savingUsBench, setSavingUsBench] = useState(false);

  const load = async () => {
    if (!company) return;
    setLoading(true);
    try { const { data } = await api.get(`/companies/${company.id}/sweat-equity`); setData(data); }
    finally { setLoading(false); }
  };
  const loadTracks = async () => {
    if (!company) return;
    try { const { data } = await api.get(`/companies/${company.id}/role-tracks`); setTracks(data); }
    catch (e) { console.error("tracks load failed", e); }
  };
  const loadBenchmarks = async () => {
    if (!company) return;
    try { const { data } = await api.get(`/companies/${company.id}/comp-benchmarks`); setBenchmarks(data); }
    catch (e) { console.error("benchmarks load failed", e); }
  };
  const loadUsBench = async () => {
    if (!company) return;
    try { const { data } = await api.get(`/companies/${company.id}/market-benchmarks`); setUsBench(data); }
    catch (e) { console.error("US benchmarks load failed", e); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); loadTracks(); loadBenchmarks(); loadUsBench(); }, [company]);

  const useBenchmark = () => {
    if (!benchmarks) return;
    const val = benchmarks.roles?.[form.role]?.[form.region];
    if (!val) { toast.info("Pick a benchmark role and region first"); return; }
    setForm((f) => ({ ...f, market_salary: val }));
    toast.success(`Set market salary to benchmark for ${form.role} · ${form.region}`);
  };

  const usValue = aiEstimate?.total_comp ?? (usRole && usLevel ? usBench?.roles?.[usRole]?.[usLevel] : null);
  const levelLabel = (k) => usBench?.levels?.find((l) => l.key === k)?.label || k;

  const useUsBenchmark = () => {
    if (!usValue) { toast.info("Pick a benchmark role and experience level first"); return; }
    setForm((f) => ({ ...f, market_salary: usValue, experience_level: usLevel, role: f.role || usRole }));
    toast.success(`Market salary set to US benchmark · ${usRole} · ${levelLabel(usLevel)}`);
  };

  const aiRefresh = async () => {
    if (!usRole || !usLevel) { toast.info("Pick a role and experience level first"); return; }
    if (needsUpgrade(company?.plan, "growth")) { openUpgrade({ plan: "growth" }); return; }
    setAiBusy(true);
    const reqId = ++aiReqId.current;
    try {
      const { data } = await api.post(`/companies/${company.id}/market-benchmarks/ai-estimate`,
        { role: usRole, level: usLevel, context: form.role && form.role !== usRole ? form.role : "" });
      if (reqId === aiReqId.current) setAiEstimate(data);
    } catch (e) {
      if (e?.response?.status !== 402) toast.error(e?.response?.data?.detail || "AI estimate failed");
    } finally { setAiBusy(false); }
  };

  const allTracks = [...(tracks.presets || []), ...(tracks.custom || [])];
  const applyTrack = (tid) => {
    const t = allTracks.find((x) => x.id === tid);
    if (!t) return;
    setForm((f) => ({ ...f, vesting_years: t.vesting_years, cliff_months: t.cliff_months, role: f.role || (t.is_preset ? f.role : t.name) }));
    toast.success(`Applied ${t.name} track · ${t.vesting_years}yr / ${t.cliff_months}mo cliff`);
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const startCreate = () => { setForm(EMPTY); setEditId(null); setAiEstimate(null); setUsRole(""); setUsLevel(""); setOpen(true); };
  const startEdit = (e) => {
    setForm({ person_name: e.person_name || "", role: e.role || "", region: e.region || "", experience_level: e.experience_level || "", market_salary: e.market_salary || "",
      actual_salary: e.actual_salary || "", start_date: (e.start_date || "").slice(0, 10), end_date: (e.end_date || "").slice(0, 10) || "",
      equity_pct: e.equity_pct ?? "", vesting_years: e.vesting_years ?? 4, cliff_months: e.cliff_months ?? 12,
      raises: (e.raises || []).map((r) => ({ _uid: uid(), effective_date: (r.effective_date || "").slice(0, 10), actual_salary: r.actual_salary })), notes: e.notes || "" });
    setAiEstimate(null); setUsRole(""); setUsLevel(e.experience_level || "");
    setEditId(e.id); setOpen(true);
  };

  const addRaise = () => setForm((f) => ({ ...f, raises: [...(f.raises || []), { _uid: uid(), effective_date: "", actual_salary: "" }] }));
  const setRaise = (i, k, v) => setForm((f) => ({ ...f, raises: f.raises.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)) }));
  const removeRaise = (i) => setForm((f) => ({ ...f, raises: f.raises.filter((_, idx) => idx !== i) }));

  const save = async () => {
    if (!form.person_name.trim()) return toast.error("Name is required");
    if (!form.start_date) return toast.error("Start date is required");
    setSaving(true);
    const payload = {
      ...form,
      market_salary: Number(form.market_salary) || 0,
      actual_salary: Number(form.actual_salary) || 0,
      equity_pct: form.equity_pct === "" ? null : Number(form.equity_pct),
      raises: (form.raises || []).filter((r) => r.effective_date).map((r) => ({ effective_date: r.effective_date, actual_salary: Number(r.actual_salary) || 0 })),
      end_date: form.end_date || null,
    };
    try {
      if (editId) await api.put(`/companies/${company.id}/sweat-equity/${editId}`, payload);
      else await api.post(`/companies/${company.id}/sweat-equity`, payload);
      toast.success(editId ? "Updated" : "Contributor added");
      setOpen(false); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/companies/${company.id}/sweat-equity/${id}`); toast.success("Removed"); load(); }
    catch { toast.error("Delete failed"); }
  };

  const downloadReport = async () => {
    setReporting(true);
    try {
      const res = await api.get(`/companies/${company.id}/sweat-equity/report`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${(company.name || "company").replace(/\s+/g, "_")}_sweat_equity_report.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success("Report downloaded");
    } catch (e) { console.error(e); toast.error("Could not generate report"); }
    finally { setReporting(false); }
  };

  // ---- Role track management ----
  const startTrackCreate = () => { setTrackForm({ name: "", vesting_years: 4, cliff_months: 12 }); setTrackEditId(null); };
  const startTrackEdit = (t) => { setTrackForm({ name: t.name, vesting_years: t.vesting_years, cliff_months: t.cliff_months }); setTrackEditId(t.id); };
  const saveTrack = async () => {
    if (!trackForm.name.trim()) return toast.error("Track name required");
    const payload = { name: trackForm.name.trim(), vesting_years: Number(trackForm.vesting_years) || 0, cliff_months: Number(trackForm.cliff_months) || 0 };
    try {
      if (trackEditId) await api.put(`/role-tracks/${trackEditId}`, payload);
      else await api.post(`/companies/${company.id}/role-tracks`, payload);
      toast.success("Track saved"); startTrackCreate(); loadTracks();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const deleteTrack = async (id) => {
    try { await api.delete(`/role-tracks/${id}`); toast.success("Track deleted"); loadTracks(); }
    catch { toast.error("Delete failed"); }
  };

  // ---- Benchmark editing ----
  const openBenchEditor = () => {
    if (!benchmarks) return;
    const draft = {};
    Object.keys(benchmarks.roles).forEach((role) => {
      draft[role] = {};
      benchmarks.regions.forEach((region) => { draft[role][region] = benchmarks.roles[role][region] ?? ""; });
    });
    setBenchDraft(draft); setBenchOpen(true);
  };
  const setBenchCell = (role, region, v) => setBenchDraft((d) => ({ ...d, [role]: { ...d[role], [region]: v } }));
  const saveBenchmarks = async () => {
    setSavingBench(true);
    const roles = {};
    Object.keys(benchDraft).forEach((role) => {
      roles[role] = {};
      Object.keys(benchDraft[role]).forEach((region) => { roles[role][region] = Number(benchDraft[role][region]) || 0; });
    });
    try {
      const { data } = await api.put(`/companies/${company.id}/comp-benchmarks`, { roles });
      setBenchmarks(data); setBenchOpen(false);
      toast.success("Benchmark salaries updated");
    } catch (e) { if (e?.response?.status !== 402) toast.error("Save failed"); }
    finally { setSavingBench(false); }
  };

  // ---- US market benchmark editing ----
  const openUsBenchEditor = () => {
    if (!usBench) return;
    const draft = {};
    Object.keys(usBench.roles).forEach((role) => {
      draft[role] = {};
      usBench.levels.forEach((l) => { draft[role][l.key] = usBench.roles[role][l.key] ?? ""; });
    });
    setUsBenchDraft(draft); setUsBenchOpen(true);
  };
  const setUsBenchCell = (role, k, v) => setUsBenchDraft((d) => ({ ...d, [role]: { ...d[role], [k]: v } }));
  const saveUsBenchmarks = async () => {
    setSavingUsBench(true);
    const roles = {};
    Object.keys(usBenchDraft).forEach((role) => {
      Object.keys(usBenchDraft[role]).forEach((k) => {
        const raw = usBenchDraft[role][k];
        if (raw === "" || raw === null) return; // blank = revert to default
        const v = Number(raw) || 0;
        if (v !== (usBench?.defaults?.[role]?.[k] ?? 0)) {
          roles[role] = roles[role] || {};
          roles[role][k] = v;
        }
      });
    });
    try {
      const { data } = await api.put(`/companies/${company.id}/market-benchmarks`, { roles });
      setUsBench(data); setUsBenchOpen(false);
      toast.success("US market benchmarks updated");
    } catch (e) { if (e?.response?.status !== 402) toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSavingUsBench(false); }
  };

  const money = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n || 0);
  const hasEntries = data.entries.length > 0;

  return (
    <AppShell
      title="Sweat Equity"
      subtitle="Show investors the real value of your team's below-market time"
      actions={
        <div className="flex items-center gap-2">
          {hasEntries && (
            <Button variant="outline" className="gap-2" onClick={downloadReport} disabled={reporting} data-testid="download-report-btn">
              {reporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Report
            </Button>
          )}
          <Button className="gap-2" onClick={startCreate} data-testid="add-sweat-btn"><Plus className="h-4 w-4" /> Add contributor</Button>
        </div>
      }
    >
      <div className="mb-4 flex items-start gap-2 rounded-lg border border-dashed bg-secondary/40 px-3 py-2 text-xs text-muted-foreground">
        <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
        <span>Founders often work for years at little or no salary. This quantifies that sacrifice as the capital it truly represents - and shares it with investors on their portal.</span>
      </div>

      <div className="mb-6 flex flex-wrap items-center gap-2">
        <Button variant="outline" size="sm" className="gap-1.5" onClick={() => { startTrackCreate(); setTracksOpen(true); }} data-testid="manage-tracks-btn">
          <Layers className="h-3.5 w-3.5" /> Role tracks
        </Button>
        <Button variant="outline" size="sm" className="gap-1.5" onClick={() => (needsUpgrade(company?.plan, "growth") ? openUpgrade({ plan: "growth" }) : openBenchEditor())} data-testid="edit-benchmarks-btn">
          <SlidersHorizontal className="h-3.5 w-3.5" /> Edit benchmarks
          {needsUpgrade(company?.plan, "growth") && <PlanBadge plan="growth" />}
        </Button>
        <Button variant="outline" size="sm" className="gap-1.5" onClick={() => (needsUpgrade(company?.plan, "growth") ? openUpgrade({ plan: "growth" }) : openUsBenchEditor())} data-testid="edit-us-benchmarks-btn">
          <Globe2 className="h-3.5 w-3.5" /> US benchmarks
          {needsUpgrade(company?.plan, "growth") && <PlanBadge plan="growth" />}
        </Button>
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : !hasEntries ? (
        <EmptyState icon={HeartHandshake} title="No contributors yet" description="Add each founder or key team member with their market vs actual salary to compute sweat equity." actionLabel="Add contributor" onAction={startCreate} />
      ) : (
        <div className="space-y-6">
          <SweatEquityCard summary={data.summary} entries={data.entries} history={data.history} variant="founder" />
          <Card className="overflow-hidden rounded-xl border bg-card">
            <div className="divide-y">
              {data.entries.map((e) => (
                <div key={e.id} className="flex items-center justify-between gap-4 p-4" data-testid={`sweat-entry-${e.id}`}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-foreground">{e.person_name}</span>
                      {e.role && <span className="text-xs text-muted-foreground">{e.role}</span>}
                      {e.experience_level && <span className="rounded-full bg-secondary px-2 py-0.5 text-[10px] font-medium text-muted-foreground">{levelLabel(e.experience_level)}</span>}
                      {e.ongoing && <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-700">ongoing</span>}
                    </div>
                    <div className="mt-0.5 text-xs text-muted-foreground">
                      Market {money(e.market_salary)} · Actual {money(e.actual_salary)} · {e.months} months · gap {money(e.annual_gap)}/yr
                      {e.raises?.length ? ` · ${e.raises.length} raise${e.raises.length > 1 ? "s" : ""}` : ""}
                      {e.equity_pct ? ` · ${e.equity_pct}% equity` : ""}
                      {e.sweat_per_pct ? ` · ${money(e.sweat_per_pct)}/1%` : ""}
                      {` · vest ${e.vesting_years ?? 4}yr/${e.cliff_months ?? 12}mo`}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <div className="h-display text-lg font-semibold tabular-nums text-primary">{money(e.accrued)}</div>
                      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">invested</div>
                    </div>
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => startEdit(e)} data-testid={`edit-sweat-${e.id}`}><Pencil className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-red-600" onClick={() => remove(e.id)} data-testid={`delete-sweat-${e.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}

      {/* Entry dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto" data-testid="sweat-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><HeartHandshake className="h-5 w-5 text-primary" /> {editId ? "Edit contributor" : "Add contributor"}</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Enter market vs actual salary, dates, equity and vesting for a team member.</DialogDescription>
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div><Label>Name</Label><Input value={form.person_name} onChange={(e) => set("person_name", e.target.value)} placeholder="Alex Founder" className="mt-1" data-testid="sweat-name" /></div>
              <div><Label>Role</Label><Input value={form.role} onChange={(e) => set("role", e.target.value)} placeholder="CEO" className="mt-1" data-testid="sweat-role" /></div>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div><Label>Market salary (annual)</Label><Input type="number" value={form.market_salary} onChange={(e) => set("market_salary", e.target.value)} placeholder="200000" className="mt-1" data-testid="sweat-market" /></div>
              <div><Label>Actual salary (annual)</Label><Input type="number" value={form.actual_salary} onChange={(e) => set("actual_salary", e.target.value)} placeholder="60000" className="mt-1" data-testid="sweat-actual" /></div>
            </div>
            {benchmarks && (
              <div className="flex items-end gap-2 rounded-lg border bg-secondary/40 p-2.5">
                <div className="flex-1">
                  <Label className="text-xs">Region (for market benchmark)</Label>
                  <Select value={form.region} onValueChange={(v) => set("region", v)}>
                    <SelectTrigger className="mt-1 h-9" data-testid="sweat-region"><SelectValue placeholder="Select region" /></SelectTrigger>
                    <SelectContent>{benchmarks.regions.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <Button type="button" variant="outline" className="h-9" onClick={useBenchmark} data-testid="use-benchmark">Use benchmark</Button>
              </div>
            )}
            {benchmarks && <p className="-mt-2 text-[11px] text-muted-foreground">Benchmark roles: {Object.keys(benchmarks.roles).join(", ")} - set the role above to match.</p>}
            {usBench && (
              <div className="rounded-lg border border-primary/20 bg-primary/5 p-3" data-testid="us-bench-block">
                <Label className="text-xs font-semibold">US market benchmark · levels.fyi-style · total comp</Label>
                <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <Select value={usRole} onValueChange={(v) => { setUsRole(v); setAiEstimate(null); aiReqId.current++; }} disabled={aiBusy}>
                    <SelectTrigger className="h-9" data-testid="us-bench-role"><SelectValue placeholder="Benchmark role" /></SelectTrigger>
                    <SelectContent>{Object.keys(usBench.roles).map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                  </Select>
                  <Select value={usLevel} onValueChange={(v) => { setUsLevel(v); setAiEstimate(null); aiReqId.current++; }} disabled={aiBusy}>
                    <SelectTrigger className="h-9" data-testid="us-bench-level"><SelectValue placeholder="Experience level" /></SelectTrigger>
                    <SelectContent>{usBench.levels.map((l) => <SelectItem key={l.key} value={l.key}>{l.label} · {l.years}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                {usValue > 0 && (
                  <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                    <div className="min-w-0">
                      <span className="h-display text-lg font-bold text-primary" data-testid="us-bench-value">{money(usValue)}</span>
                      <span className="ml-1 text-[11px] text-muted-foreground">/yr total comp{aiEstimate ? " · AI estimate" : ""}</span>
                      {aiEstimate && (
                        <p className="mt-0.5 text-[11px] text-muted-foreground" data-testid="us-bench-ai-rationale">
                          {aiEstimate.low > 0 && aiEstimate.high > 0 ? `Range ${money(aiEstimate.low)}–${money(aiEstimate.high)}. ` : ""}{aiEstimate.rationale}
                        </p>
                      )}
                    </div>
                    <div className="flex shrink-0 gap-2">
                      <Button type="button" variant="outline" size="sm" className="h-8 gap-1" onClick={aiRefresh} disabled={aiBusy} data-testid="us-bench-ai-btn">
                        {aiBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} AI estimate
                        {needsUpgrade(company?.plan, "growth") && <PlanBadge plan="growth" />}
                      </Button>
                      <Button type="button" size="sm" className="h-8" onClick={useUsBenchmark} data-testid="use-us-benchmark">Use as market salary</Button>
                    </div>
                  </div>
                )}
                <p className="mt-1.5 text-[10px] text-muted-foreground">{usBench.note}</p>
              </div>
            )}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div><Label>Start date</Label><DatePicker value={form.start_date} onChange={(v) => set("start_date", v)} className="mt-1" data-testid="sweat-start" /></div>
              <div><Label>End date (blank = ongoing)</Label><DatePicker value={form.end_date} onChange={(v) => set("end_date", v)} placeholder="Ongoing" className="mt-1" data-testid="sweat-end" /></div>
            </div>
            <div className="rounded-lg border bg-secondary/40 p-2.5">
              <Label className="text-xs">Role track (applies a vesting schedule)</Label>
              <Select value="" onValueChange={applyTrack}>
                <SelectTrigger className="mt-1 h-9" data-testid="sweat-track-select"><SelectValue placeholder="Apply a role track…" /></SelectTrigger>
                <SelectContent>
                  {(tracks.presets || []).map((t) => <SelectItem key={t.id} value={t.id}>{t.name} · {t.vesting_years}yr / {t.cliff_months}mo{" "}(preset)</SelectItem>)}
                  {(tracks.custom || []).map((t) => <SelectItem key={t.id} value={t.id}>{t.name} · {t.vesting_years}yr / {t.cliff_months}mo</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div><Label>Equity owned (%)</Label><Input type="number" step="0.01" value={form.equity_pct} onChange={(e) => set("equity_pct", e.target.value)} placeholder="e.g. 25" className="mt-1" data-testid="sweat-equity-pct" /></div>
              <div><Label>Vesting (years)</Label><Input type="number" value={form.vesting_years} onChange={(e) => set("vesting_years", Number(e.target.value))} className="mt-1" data-testid="sweat-vesting-years" /></div>
              <div><Label>Cliff (months)</Label><Input type="number" value={form.cliff_months} onChange={(e) => set("cliff_months", Number(e.target.value))} className="mt-1" data-testid="sweat-cliff" /></div>
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label>Backdated raises</Label>
                <Button type="button" variant="ghost" size="sm" className="h-7 gap-1 px-2 text-xs" onClick={addRaise} data-testid="add-raise"><Plus className="h-3.5 w-3.5" /> Add raise</Button>
              </div>
              <p className="mb-2 text-xs text-muted-foreground">Record salary changes over time so accrual reflects raises, not one flat rate.</p>
              <div className="space-y-2">
                {(form.raises || []).map((r, i) => (
                  <div key={r._uid || i} className="flex items-center gap-2" data-testid={`raise-row-${i}`}>
                    <DatePicker value={r.effective_date} onChange={(v) => setRaise(i, "effective_date", v)} className="flex-1" data-testid={`raise-date-${i}`} />
                    <Input type="number" value={r.actual_salary} onChange={(e) => setRaise(i, "actual_salary", e.target.value)} placeholder="New salary" className="flex-1" data-testid={`raise-salary-${i}`} />
                    <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-red-600" onClick={() => removeRaise(i)} data-testid={`remove-raise-${i}`}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="gap-2" data-testid="sweat-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} {editId ? "Save" : "Add"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Role tracks dialog */}
      <Dialog open={tracksOpen} onOpenChange={setTracksOpen}>
        <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto" data-testid="tracks-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Layers className="h-5 w-5 text-primary" /> Role tracks</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Create and manage reusable vesting schedules.</DialogDescription>
          <p className="text-xs text-muted-foreground">Reusable vesting schedules. Apply a track when adding a contributor to preset years &amp; cliff.</p>
          <div className="space-y-2">
            <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Presets</p>
            {(tracks.presets || []).map((t) => (
              <div key={t.id} className="flex items-center justify-between rounded-lg border bg-secondary/30 px-3 py-2 text-sm" data-testid={`track-preset-${t.name}`}>
                <span className="font-medium">{t.name}</span>
                <span className="text-xs text-muted-foreground">{t.vesting_years}yr vest · {t.cliff_months}mo cliff</span>
              </div>
            ))}
            {(tracks.custom || []).length > 0 && <p className="pt-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Your tracks</p>}
            {(tracks.custom || []).map((t) => (
              <div key={t.id} className="flex items-center justify-between rounded-lg border px-3 py-2 text-sm" data-testid={`track-custom-${t.id}`}>
                <span className="font-medium">{t.name}</span>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">{t.vesting_years}yr · {t.cliff_months}mo</span>
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => startTrackEdit(t)} data-testid={`edit-track-${t.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-red-600" onClick={() => deleteTrack(t.id)} data-testid={`delete-track-${t.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              </div>
            ))}
          </div>
          <div className="rounded-lg border bg-secondary/40 p-3">
            <p className="mb-2 text-xs font-medium">{trackEditId ? "Edit track" : "New custom track"}</p>
            <div className="grid grid-cols-3 gap-2">
              <div className="col-span-1"><Label className="text-xs">Name</Label><Input value={trackForm.name} onChange={(e) => setTrackForm((f) => ({ ...f, name: e.target.value }))} placeholder="Intern" className="mt-1 h-9" data-testid="track-name" /></div>
              <div><Label className="text-xs">Years</Label><Input type="number" value={trackForm.vesting_years} onChange={(e) => setTrackForm((f) => ({ ...f, vesting_years: e.target.value }))} className="mt-1 h-9" data-testid="track-years" /></div>
              <div><Label className="text-xs">Cliff (mo)</Label><Input type="number" value={trackForm.cliff_months} onChange={(e) => setTrackForm((f) => ({ ...f, cliff_months: e.target.value }))} className="mt-1 h-9" data-testid="track-cliff" /></div>
            </div>
            <div className="mt-3 flex justify-end gap-2">
              {trackEditId && <Button variant="outline" size="sm" onClick={startTrackCreate}>Cancel edit</Button>}
              <Button size="sm" className="gap-1.5" onClick={saveTrack} data-testid="save-track-btn"><Plus className="h-3.5 w-3.5" /> {trackEditId ? "Save track" : "Add track"}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Benchmark editor dialog */}
      <Dialog open={benchOpen} onOpenChange={setBenchOpen}>
        <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto" data-testid="benchmarks-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><SlidersHorizontal className="h-5 w-5 text-primary" /> Edit benchmark salaries</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Adjust market salary references per role and region.</DialogDescription>
          <p className="text-xs text-muted-foreground">Tune market salary references to match your own data. These power the "Use benchmark" button.</p>
          {benchmarks && (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-xs">
                <thead>
                  <tr>
                    <th className="border-b p-2 text-left font-medium text-muted-foreground">Role</th>
                    {benchmarks.regions.map((r) => <th key={r} className="border-b p-2 text-left font-medium text-muted-foreground">{r}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {Object.keys(benchDraft).map((role) => (
                    <tr key={role}>
                      <td className="p-1.5 font-medium">{role}</td>
                      {benchmarks.regions.map((region) => (
                        <td key={region} className="p-1.5">
                          <Input type="number" value={benchDraft[role]?.[region] ?? ""} onChange={(e) => setBenchCell(role, region, e.target.value)} className="h-8 w-28" data-testid={`bench-${role}-${region}`} />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setBenchOpen(false)}>Cancel</Button>
            <Button onClick={saveBenchmarks} disabled={savingBench} className="gap-2" data-testid="save-benchmarks-btn">
              {savingBench ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Save benchmarks
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* US market benchmark editor dialog */}
      <Dialog open={usBenchOpen} onOpenChange={setUsBenchOpen}>
        <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto" data-testid="us-benchmarks-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Globe2 className="h-5 w-5 text-primary" /> US market benchmarks · levels.fyi-style</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Adjust US total-comp benchmarks per role and experience level.</DialogDescription>
          <p className="text-xs text-muted-foreground">{usBench?.note} These power the "Use as market salary" button in the contributor form.</p>
          {usBench && (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-xs">
                <thead>
                  <tr>
                    <th className="border-b p-2 text-left font-medium text-muted-foreground">Role</th>
                    {usBench.levels.map((l) => (
                      <th key={l.key} className="border-b p-2 text-left font-medium text-muted-foreground">{l.label}<br /><span className="font-normal">{l.years}</span></th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {Object.keys(usBenchDraft).map((role) => (
                    <tr key={role}>
                      <td className="p-1.5 font-medium">{role}</td>
                      {usBench.levels.map((l) => (
                        <td key={l.key} className="p-1.5">
                          <Input type="number" value={usBenchDraft[role]?.[l.key] ?? ""} onChange={(e) => setUsBenchCell(role, l.key, e.target.value)} className="h-8 w-24" data-testid={`us-bench-${role}-${l.key}`} />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setUsBenchOpen(false)}>Cancel</Button>
            <Button onClick={saveUsBenchmarks} disabled={savingUsBench} className="gap-2" data-testid="save-us-benchmarks-btn">
              {savingUsBench ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Save US benchmarks
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
